import React, { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import deepEqual from "react-fast-compare";

import { ClientState } from "state/state";
import { isAtNode } from "state/node/selectors";
import { contactsPrepare, visitedContactsDelete, visitedContactsPrepare } from "state/contacts/actions";
import { getContacts, getVisitedContacts } from "state/contacts/selectors";
import { jumpFar, jumpNear } from "state/navigation/actions";
import { isAtSearchPage } from "state/navigation/selectors";
import { SearchTab } from "state/search/state";
import { searchHistoryDelete, searchHistoryPrepare } from "state/search/actions";
import { getSearchNodeName, getSearchQuery, isSearchInBlogAvailable } from "state/search/selectors";
import { useSuggestions, useDispatcher } from "ui/hook";
import { SearchListItem } from "ui/mainmenu/search/SearchSuggestions";
import { namesListQuery } from "util/names-list";
import { ut } from "util/url";

interface UseSearchSuggestionsProps {
    onSubmit?: (success: boolean) => void;
}

interface UseSearchSuggestionsResult {
    query: string | null;
    searchList: SearchListItem[];
    selectedIndex: number;
    focused: boolean;
    handleKeyDown: (event: React.KeyboardEvent) => void;
    handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleFocus: () => void;
    handleBlur: () => void;
    handleClear: () => void;
    handleClick: (index: number) => () => void;
    submit: (success: boolean, item: SearchListItem) => void;
    handleHistoryDelete: (index: number) => (event: React.MouseEvent) => void;
    inputDom: React.RefObject<HTMLInputElement | null>;
    listDom: React.RefObject<HTMLDivElement | null>;
}

export function useSearchSuggestions(
    {onSubmit: onSubmitCallback}: UseSearchSuggestionsProps = {}
): UseSearchSuggestionsResult {
    const contactNames = useSelector(getContacts);
    const visitedContactNames = useSelector(getVisitedContacts);
    const history = useSelector((state: ClientState) => state.search.history);
    const atNode = useSelector(isAtNode);
    const atSearch = useSelector(isAtSearchPage);
    const searchNode = useSelector(getSearchNodeName);
    const defaultQuery = useSelector(getSearchQuery);
    const defaultTab: SearchTab = useSelector(isSearchInBlogAvailable) ? "current-blog" : "content";
    const dispatch = useDispatcher();

    const [focused, setFocused] = React.useState<boolean>(false);
    const inputBlurTimeout = useRef<number | NodeJS.Timeout | null>(null);
    const inputDom = useRef<HTMLInputElement>(null);
    const listDom = useRef<HTMLDivElement>(null);

    const onInputFocus = () => setFocused(true);

    const onInputBlur = () => {
        inputBlurTimeout.current = setTimeout(() => {
            setFocused(false);
            inputBlurTimeout.current = null;
        }, 200);
    }

    const onSubmit = (success: boolean, item: SearchListItem) => {
        if (success) {
            switch (item.type) {
                case "name":
                    inputDom.current?.blur();
                    if (item.nodeName != null) {
                        setQuery("");
                        dispatch(jumpFar(item.nodeName, null, "/timeline", null, null));
                    }
                    break;
                case "search":
                    inputDom.current?.blur();
                    if (query) {
                        if (atNode) {
                            dispatch(jumpNear("/search", ut`query=${query}&tab=${defaultTab}`, null));
                        } else {
                            dispatch(jumpFar(searchNode, null, "/search", ut`query=${query}`, null));
                        }
                    }
                    break;
                case "history":
                    inputDom.current?.blur();
                    dispatch(jumpNear("/search", ut`query=${item.query}&tab=${defaultTab}`, null));
                    break;
            }
        } else {
            inputDom.current?.blur();
        }
        onSubmitCallback && onSubmitCallback(success);
    }

    const {
        searchList, setSearchList, selectedIndex, query, setQuery, handleKeyDown, handleChange, handleClick
    } = useSuggestions<SearchListItem>({
        defaultQuery: atSearch ? defaultQuery : "",
        runQuery: query => {
            dispatch(visitedContactsPrepare(query));
            dispatch(contactsPrepare(query));
            dispatch(searchHistoryPrepare(query));
        },
        onSubmit: (_, success, result) => onSubmit(success, result ?? {type: "search"}),
        submitOnEscape: true,
        inputDom,
        listDom
    });

    const onClear = () => {
        setQuery("");
        if (inputBlurTimeout.current != null) {
            clearTimeout(inputBlurTimeout.current);
            inputBlurTimeout.current = null;
        }
        inputDom.current?.focus();
    }

    const onHistoryDelete = (index: number) => (event: React.MouseEvent) => {
        const item = searchList[index];
        if (item.type === "history") {
            dispatch(searchHistoryDelete(item.query));
            setSearchList(list => list.toSpliced(index, 1));
        } else if (item.type === "name" && item.nodeName != null) {
            dispatch(visitedContactsDelete(item.nodeName));
            setSearchList(list => list.toSpliced(index, 1));
        }
        event.stopPropagation();
        event.preventDefault();
    }

    useEffect(() => {
        setSearchList(list => {
            const newList: SearchListItem[] = [];

            const historical = history
                .filter(sh => sh.query.startsWith(query ?? ""))
                .sort((c1, c2) => c2.createdAt - c1.createdAt)
                .map(sh => ({type: "history" as const, query: sh.query}));
            if (historical.length > 3) {
                historical.splice(3);
            }
            newList.push(...historical);

            const visitedNames = namesListQuery(
                visitedContactNames.toSorted((c1, c2) => c1.distance - c2.distance),
                query
            )
                .map(item => ({type: "name" as const, ...item}));
            const visitedNodeNames = new Set(visitedNames.map(item => item.nodeName));
            const names: SearchListItem[] = [
                ...visitedNames,
                ...namesListQuery(
                    contactNames.toSorted((c1, c2) => c1.distance - c2.distance),
                    query
                )
                    .filter(item => !visitedNodeNames.has(item.nodeName))
                    .map(item => ({type: "name" as const, ...item}))
            ];
            if (query && names.length > 5) {
                names.splice(5);
            }
            newList.push(...names);

            if (query) {
                newList.push({type: "search" as const});
            }

            return deepEqual(list, newList) ? list : newList;
        });
    }, [contactNames, history, query, setSearchList, visitedContactNames]);

    return {
        query, searchList, selectedIndex, focused, handleKeyDown, handleChange,
        handleFocus: onInputFocus, handleBlur: onInputBlur, handleClear: onClear, handleClick, submit: onSubmit,
        handleHistoryDelete: onHistoryDelete, inputDom, listDom
    };
}
